import {
	ContextMenu,
	ContextMenuDeleteItem,
	ContextMenuItem,
	IDrawerDataListColumn,
	PaginatedDrawerAutoDataList,
	isSuccess,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelDeleteEntity,
	useToasts,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PermissionName } from "../../../shared/types/permission";
import { accessTokenService } from "../shared/api/accessTokenService";
import {
	AccessTokenFormValues,
	AccessTokenIndexResource,
	AccessTokenPermissionsFormValues,
} from "../shared/types/accessToken";
import { AccessTokenCreateDrawer, AccessTokenCreatedDrawer, AccessTokenPermissionsDrawer } from "./AccessTokenDrawer";

export const AccessTokenList: FC = () => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { showToast } = useToasts();
	const [createdToken, setCreatedToken] = useState<string | null>(null);

	const canCreate = useHasPermission<PermissionName>(["accessToken.create"]);
	const canUpdate = useHasPermission<PermissionName>(["accessToken.update"]);

	const deleteToken = useLaravelDeleteEntity(accessTokenService, {
		entityName: t("Access token"),
		message: t("Access token was successfully deleted."),
	});

	const columns: IDrawerDataListColumn<AccessTokenIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
			},
			{
				heading: t("Expiry date"),
				valueKey: "expiresAt",
				type: "value",
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }, openDrawer) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuItem
							title={t("Edit permissions")}
							label={t("Edit permissions")}
							action={openDrawer}
							icon="edit"
							shortcut="primary"
						/>
						<ContextMenuDeleteItem
							action={() => deleteToken(id)}
							title={t("Delete access token")}
							label={t("Delete access token")}
							heading={t("Delete")}
							labels={{ close: t("Close"), confirm: t("Confirm") }}
						>
							{t("Are you sure you want to delete this access token?")}
						</ContextMenuDeleteItem>
					</ContextMenu>
				),
			},
		],
		[deleteToken, t],
	);

	return (
		<>
			<PaginatedDrawerAutoDataList
				canCreate={canCreate}
				canUpdate={canUpdate}
				columns={columns}
				listFetchConfig={laravelPaginatedListFetchConfig(accessTokenService.listKey, accessTokenService.index)}
				renderCreateDrawer={(drawerProps) => (
					<AccessTokenCreateDrawer
						{...drawerProps}
						onSubmit={async (values: AccessTokenFormValues) => {
							const response = await accessTokenService.create(values);

							if (isSuccess(response)) {
								await queryClient.invalidateQueries({ queryKey: accessTokenService.indexKey });
								showToast({
									type: "success",
									title: t("Access token"),
									body: t("Access token was successfully created."),
								});
								setCreatedToken(response.data.data.token);
								return true;
							}

							return false;
						}}
					/>
				)}
				renderUpdateDrawer={(drawerProps) => (
					<AccessTokenPermissionsDrawer
						{...drawerProps}
						onSubmit={async (values: AccessTokenPermissionsFormValues) => {
							const response = await accessTokenService.syncPermissions(drawerProps.id, values);

							if (isSuccess(response)) {
								await queryClient.invalidateQueries({ queryKey: accessTokenService.indexKey });
								return true;
							}

							return false;
						}}
					/>
				)}
				translations={{
					title: t("Access tokens"),
					empty: t("No access tokens available"),
				}}
			/>
			<AccessTokenCreatedDrawer
				open={createdToken !== null}
				token={createdToken ?? ""}
				onClose={() => setCreatedToken(null)}
			/>
		</>
	);
};
